import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/auth";
import { errMsg, fmtDateTime } from "@/lib/residentApi";
import { ErrorLine } from "@/components/nds/windows/resident/ui";
import { DupeCompare } from "./DupeCompare";

const KINDS = [["resident", "Residents"], ["document", "Files & documents"], ["note", "Case notes"], ["checkin", "Check-ins"], ["staff", "Staff"], ["partner", "Partners"]];
const title = (r) => r.name || r.file_name || r.preview || r.type || r.id;
const sub = (r) => [r.resident && r.resident !== r.name ? r.resident : null, r.status, r.date || (r.created_at || "").slice(0, 10), r.email, r.label].filter(Boolean).join(" · ");

function Recent({ rows }) {
  return (
    <div data-testid="dupes-recent" className="border-t border-white/10 pt-3">
      <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-nex-muted mb-2">Recent hard deletes</div>
      {!rows.length ? <div className="text-xs text-nex-muted">None yet.</div> : <ul className="space-y-1">{rows.map((r) => (
        <li key={r.id} className="text-xs text-nex-silver flex flex-wrap gap-x-3"><span>{r.actor?.name}</span><span className="text-nex-muted">{fmtDateTime(r.at)}</span>
          <span>{r.changes?.kind} · {r.target_label}</span><span className="text-nex-muted">{Object.values(r.changes?.removed || {}).reduce((a, b) => a + b, 0)} row(s)</span></li>))}</ul>}
    </div>
  );
}

export function DuplicateRecords() {
  const [counts, setCounts] = useState(null);
  const [kind, setKind] = useState("resident");
  const [groups, setGroups] = useState(null);
  const [open, setOpen] = useState(null);
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState(null);
  const loadCounts = () => apiClient.get("/dupes").then((r) => setCounts(r.data.kinds)).catch((e) => setError(errMsg(e)));
  const loadGroups = (k) => { setGroups(null); setOpen(null); apiClient.get(`/dupes/${k}`).then((r) => setGroups(r.data.groups)).catch((e) => setError(errMsg(e))); };
  const loadRecent = () => apiClient.get("/dupes/log/recent").then((r) => setRecent(r.data.rows)).catch(() => {});
  useEffect(() => { loadCounts(); loadRecent(); }, []);
  useEffect(() => { loadGroups(kind); }, [kind]);
  const done = () => { loadGroups(kind); loadCounts(); loadRecent(); };
  return (
    <div data-testid="duplicate-records" className="space-y-3">
      <p className="text-xs text-nex-muted">Possible duplicates: records that look entered twice. Compare a pair side by side, pick the one to keep, then hard delete the other. Deletes are permanent and logged.</p>
      <div className="flex flex-wrap gap-1.5">{KINDS.map(([k, l]) => (
        <button key={k} type="button" onClick={() => setKind(k)} aria-pressed={kind === k} data-testid={`dupes-kind-${k}`}
          className={`px-2.5 py-1 rounded-full border font-mono text-[9px] uppercase tracking-[0.14em] transition-colors ${kind === k ? "border-nex-gold text-nex-gold" : "border-white/10 text-nex-muted hover:text-nex-silver"}`}>
          {l} <span data-testid={`dupes-count-${k}`} className={counts?.[k] ? "text-amber-300" : ""}>{counts ? counts[k] : "…"}</span></button>))}</div>
      <ErrorLine testId="dupes-error">{error}</ErrorLine>
      {!groups ? <div className="text-xs text-nex-muted">Looking for duplicates…</div> : !groups.length ? <div data-testid="dupes-empty" className="text-sm text-emerald-300">No possible duplicates found here.</div> : (
        <ul data-testid="dupes-groups" className="space-y-2">{groups.map((g, i) => {
          const gid = g.records.map((r) => r.id).join("|");
          return (
            <li key={gid} data-testid={`dupes-group-${i}`} className="border border-white/10 rounded-sm">
              <div className="flex flex-wrap items-center gap-3 px-3 py-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-amber-300">{g.match}</span>
                <span className="text-xs text-nex-muted">{g.records.length} records</span>
                <button type="button" onClick={() => setOpen(open === gid ? null : gid)} data-testid={`dupes-compare-${i}`} className="ml-auto px-2.5 py-1 border border-nex-gold/40 text-nex-gold rounded-sm font-mono text-[9px] uppercase tracking-[0.14em] hover:bg-nex-gold/10 transition-colors">{open === gid ? "Hide" : "Compare"}</button>
              </div>
              <ul className="px-3 pb-2 space-y-0.5">{g.records.map((r) => <li key={r.id} className="text-xs"><span className="text-nex-silver">{title(r)}</span> <span className="text-nex-muted">{sub(r)}</span></li>)}</ul>
              {open === gid && <div className="p-3 pt-0"><DupeCompare kind={kind} group={g} onDone={done} onClose={() => setOpen(null)} /></div>}
            </li>);
        })}</ul>)}
      <Recent rows={recent} />
    </div>
  );
}
